<template>
  <div>
    <BaseDivider color="v1-grey-3" />
    <div class="header-settings mt-24 mb-24">
      <span class="qrw-heading-md">
        {{ $t('dish_settings.title') }}
      </span>

      <small class="save-status orange-4-color" v-if="isProductSaving">
        {{ $t('dish_settings.status_saving') }}
      </small>
      <small
        :class="['save-status', isDishValid ? 'green-5-color' : 'red-4-color']"
        @click="toggleShowError"
        v-else
        >{{
          $t(
            isDishValid
              ? 'dish_settings.status_saved'
              : 'dish_settings.status_error'
          )
        }}</small
      >
    </div>
    <div class="iqos-grid">
      <div class="iqos-column">
        <div class="menu-iqos dish-fill-info">
          <div class="position-relative">
            <img
              :src="
                (dish.image || '/icons/no-dish.png') + `?nonce=${Date.now()}`
              "
              class="dish-img"
            />

            <div class="dish-image-buttons mt-16 gap-column-xs">
              <ImageUploader
                class="w-100"
                @success="onImageChange"
                @start="onStartImageUpload"
                v-if="!dish.image"
              >
                <BaseButton
                  class="w-100"
                  variant="secondary"
                  icon="plus"
                  color="green-5"
                  size="md"
                >
                  {{ $t('image_uploader.default') }}
                </BaseButton>
              </ImageUploader>
              <!-- <BaseButton
                @click="dishEditorModal = true"
                class="w-100"
                icon="pencil"
                variant="secondary"
                color="orange-6"
                size="md"
                v-if="dish.image"
              >
                {{ $t('image_uploader.edit') }}
              </BaseButton> -->
              <BaseButton
                @click="onDeletePhoto"
                class="w-100"
                v-if="dish.image"
                variant="secondary"
                icon="trash"
                color="red-4"
                size="md"
              >
                {{ $t('image_uploader.delete') }}
              </BaseButton>
              <!-- <ImageUploader
                class="upload-button"
                @success="onImageChange"
                @start="onStartImageUpload"
                :can_delete="!!dish.image"
                :can_edit="!!dish.image && !dish.image.includes('joinposter')"
                @edit="dishEditorModal = true"
                @delete="onDeletePhoto"
              /> -->
            </div>
          </div>

          <div class="iqos-tooltip-helper-box w-100">
            <BaseInput
              type="text"
              class="mt-16"
              :placeholder="isLocalizing ? dish.name : $t('menu_settings.name')"
              fullWidth
              v-model="dishName"
              @focus="name_input_focused = true"
              :valid="isLocalizing ? true : !!dishName"
              @blur="onDishNameChanged"
            />
            <IQOSTooltip
              :backdrop="false"
              :style="{ top: '-36px' }"
              earPosition="top-right"
              class="w-100"
              v-if="
                name_input_focused &&
                  isUiMenuTranslationPreviewed &&
                  isLocalizing &&
                  dish.name
              "
            >
              {{ dish.name }}
            </IQOSTooltip>
          </div>

          <div class="iqos-tooltip-helper-box w-100">
            <IQOSTooltip
              :backdrop="false"
              :style="{ top: '16px' }"
              earPosition="top-right"
              class="w-100 desc-tooltip-translation"
              v-if="
                desc_input_focused &&
                  isUiMenuTranslationPreviewed &&
                  isLocalizing &&
                  dish.description
              "
            >
              {{ dish.description }}
            </IQOSTooltip>
            <BaseTextarea
              class="mt-16 w-100"
              :rows="8"
              :placeholder="
                isLocalizing ? dish.description : $t('menu_settings.about')
              "
              v-model="dishDescription"
              @focus="desc_input_focused = true"
              @blur="
                emitUpdate();
                desc_input_focused = false;
              "
            ></BaseTextarea>
          </div>

          <div class="gap-row-1xs mt-16" v-if="isUsingServiceReservations">
            <div class="qrw-label-sm qrw-text-content-base-tertiary bold">
              {{ $t('dish_settings.service_duration') }}
            </div>
            <BaseNumberInput
              v-model="dishServiceDuration"
              placeholder="0"
              :unit="$t('units.mins')"
            />
          </div>

          <div class="gap-row-1xs mt-16" v-if="isUsingServiceReservations">
            <div class="qrw-label-sm qrw-text-content-base-tertiary bold">
              {{ $t('dish_settings.service_break_time') }}
            </div>
            <BaseNumberInput
              v-model="dishServiceBreakTime"
              placeholder="0"
              :unit="$t('units.mins')"
            />
          </div>

          <div
            class="qrw-label-sm qrw-text-content-base-tertiary bold mt-16"
            v-if="isUsingRegularDishUi"
          >
            {{ $t('dish_settings.waiting_time_ph') }}
          </div>

          <div class="waiting-time-flex mt-8" v-if="isUsingRegularDishUi">
            <BaseUnitInput
              :unitPlaceholder="$t('units.hours')"
              fullWidth
              type="number"
              @blur="emitUpdate"
              v-model.number="dishWaitingTimeHours"
              class="w-100"
            />
            <BaseUnitInput
              :unitPlaceholder="$t('units.mins')"
              fullWidth
              @blur="emitUpdate"
              type="number"
              v-model.number="dishWaitingTimeMinutes"
              class="w-100 ml-8"
            />
          </div>

          <div
            class="qrw-label-sm qrw-text-content-base-tertiary bold mt-16"
            v-if="isUsingRegularDishUi"
          >
            {{ $t('menu_settings.weight') }}
          </div>

          <BaseUnitInput
            type="number"
            v-model.number="dish.base_weight"
            :placeholder="0"
            @blur="onBaseWeightUpdated"
            :unitPlaceholder="$t('units.' + dish.unit)"
            fullWidth
            class="w-100 mt-8"
            v-if="isUsingRegularDishUi"
          />

          <div class="qrw-label-sm qrw-text-content-base-tertiary bold mt-16">
            {{ $t('menu_settings.price') }}
          </div>

          <BaseUnitInput
            type="number"
            v-model.number="dish.base_price"
            :placeholder="0"
            @blur="onBasePriceUpdated"
            :unitPlaceholder="currency.symbol"
            fullWidth
            class="w-100 mt-8"
            :disabled="blockPriceEdit"
          />
        </div>

        <ProductEnabledModes />
      </div>

      <div class="iqos-column">
        <PosLinkBlock
          @update="emitUpdate"
          v-if="hasPosterIntegration || hasIikoIntegration"
        />

        <IQOS :title="$t('dish_settings.limits')" required>
          <SlideToggleRow
            class="mt-16"
            v-model="dishStock"
            :readonly="!canChangeStock"
            >{{ $t('dish_settings.in_stock') }}</SlideToggleRow
          >
          <p class="iqos-comment qrw-paragraph-md mt-16" v-if="!canChangeStock">
            {{ $t('dish_settings.stock_disabled') }}
          </p>
          <BaseDivider class="mt-16" color="v1-grey-4" />
          <SlideToggleRow class="mt-16" v-model="dishExciseTag">{{
            $t('menu_settings.excise_goods')
          }}</SlideToggleRow>
          <p class="iqos-comment qrw-paragraph-md mt-16">
            {{ $t('dish_settings.excise_desc') }}
          </p>
        </IQOS>

        <IQOS
          :title="$t('dish_settings.weight_type')"
          v-if="isUsingRegularDishUi"
          required
        >
          <BaseTabSwitcher
            v-model="dishWeightFlag"
            class="w-100 mt-16"
            size="md"
            :tabs="[
              {
                text: $t('menu_settings.portions_goods'),
                value: 0,
              },
              {
                text: $t('menu_settings.semi_weight_goods'),
                value: 2,
              },
              {
                text: $t('menu_settings.weight_goods'),
                value: 1,
              },
            ]"
          />

          <p
            class="iqos-comment qrw-paragraph-md mt-16"
            v-if="dishWeightFlag == 0"
          >
            {{ $t('menu_settings.portion_desc') }}
          </p>

          <p
            class="iqos-comment qrw-paragraph-md mt-16"
            v-if="dishWeightFlag == 2"
          >
            {{ $t('menu_settings.semi_weight_desc') }}
          </p>

          <p
            class="iqos-comment qrw-paragraph-md mt-16"
            v-if="dishWeightFlag == 1"
          >
            {{ $t('menu_settings.weight_desc') }}
          </p>
        </IQOS>

        <IQOS :title="$t('units.units')" v-if="isUsingRegularDishUi" required>
          <BaseTabSwitcher
            class="w-100 mt-16"
            size="md"
            v-model="dishUnit"
            :tabs="[
              {
                text: $t('dish_settings.long_units.g'),
                value: 'g',
              },
              {
                text: $t('dish_settings.long_units.kg'),
                value: 'kg',
              },
              {
                text: $t('dish_settings.long_units.l'),
                value: 'l',
              },
              {
                text: $t('dish_settings.long_units.ml'),
                value: 'ml',
              },
            ]"
          />
        </IQOS>

        <DishPackaging class="menu-iqos" v-if="showPackaging" />

        <DishAR v-if="showDishAR" />

        <TaxSelector v-model="dishTaxId" inProduct />
      </div>
      <DangerConfirmModal
        @cancel="deleting = false"
        @confirm="onDelete"
        v-if="deleting"
        >{{ $t('dish_tab.confirm_delete') }}</DangerConfirmModal
      >

      <div class="blur-box" v-if="dishEditorModal"></div>

      <DishPhotoEditorModal2
        :image_url="dish.image_original || dish.image"
        @success="onPhotosEdited"
        @close="dishEditorModal = false"
        v-if="dishEditorModal"
      />

      <DangerConfirmModal
        @cancel="deleting = false"
        @confirm="onDelete"
        v-if="deleting"
        >{{ $t('dish_tab.confirm_delete') }}</DangerConfirmModal
      >

      <TransferDishModal
        @close="transfer_modal = false"
        @transfered="onDishTransfered"
        v-if="transfer_modal"
      />
    </div>
  </div>
</template>

<script>
import ImageUploader from '@/components/ImageUploader.vue';

import ModificatorSettings from '@/components/menu/ModificatorSettings.vue';
import CircleToggle from '@/components/CircleToggle.vue';
import RoundCheckbox from '@/components/RoundCheckbox.vue';
import DishPhotoEditorModal2 from '@/components/modals/PhotoEditorModal.vue';
import PosterMenuInput from '@/components/integrations/PosterMenuInput.vue';
import BlackSelect from '@/components/BlackSelect.vue';
import DishPackaging from '@/components/menu/DishPackagingAttachment.vue';
import ProductEnabledModes from '@/components/menu/ProductEnabledModes.vue';
import TransferDishModal from '@/components/menu/TransferDishModal.vue';
import DashboardButton from '@/components/DashboardButton.vue';
import PosLinkBlock from '@/components/integrations/PosLinkBlock.vue';
import SlideToggleRow from '@/components/SlideToggleRow.vue';
import DishAR from '@/components/menu/DishAR.vue';
import TaxSelector from '@/components/menu/TaxSelector.vue';

export default {
  components: {
    ImageUploader,
    ModificatorSettings,
    CircleToggle,
    RoundCheckbox,
    DishPhotoEditorModal2,
    PosterMenuInput,
    BlackSelect,
    DishPackaging,
    ProductEnabledModes,
    TransferDishModal,
    DashboardButton,
    PosLinkBlock,
    SlideToggleRow,
    DishAR,
    TaxSelector,
  },
  data() {
    return {
      deleting: false,
      pending_uploads: {},
      showModificators: true,
      dishEditorModal: false,
      transfer_modal: false,
      select_from_poster: false,

      name_input_focused: false,
      desc_input_focused: false,
    };
  },
  props: {
    // showModificators: Boolean,
    category: Object,
  },
  methods: {
    onDelete() {
      const category_name = this.dish.category;

      this.deleting = false;
      this.$store.commit('menu/removeProduct', this.dish);
      this.$store.commit('menu/chooseProduct', null);
      this.$store.dispatch('menu/save');

      this.chooseNearestProduct(category_name);
    },
    chooseNearestProduct(category_name) {
      const other_products = this.$store.getters['menu/productsForCategory']({
        name: category_name,
      });

      if (other_products.length) {
        this.$store.commit('menu/chooseProduct', other_products[0]);
      } else {
        this.$store.commit('menu/chooseProduct', null);
      }
    },
    onImageChange(sizes, upload_id) {
      const product = this.pending_uploads[upload_id];

      if (!product) return;

      this.$store.commit('menu/setProductImageByProduct', {
        url: sizes.lg,
        product,
      });
      this.$store.commit('menu/setProductImageByProduct', {
        url: sizes.sm,
        product,
        thumbnail: true,
      });
      this.$store.commit('menu/setProductImageByProduct', {
        url: sizes.original,
        product,
        original: true,
      });
      this.emitUpdate();
      this.$delete(this.pending_uploads, upload_id);

      this.$store.dispatch('auditlog/write', {
        type: 'change_menu_product_photo',
        payload: {
          menu_id: this.menu._id,
          dish_name: this.dish.name,
          dish_id: this.dish._id,
          photo_url: sizes.original,
        },
      });

      this.$emit('imagesUpdated');

      /*this.$nextTick(() => {
        this.dishEditorModal = true;
        this.$emit("imagesUpdated");
      });*/
    },
    onDeletePhoto() {
      this.$store.commit('menu/setProductImageByProduct', {
        url: null,
        product: this.dish,
      });

      this.$store.commit('menu/setProductImageByProduct', {
        url: null,
        product: this.dish,
        thumbnail: true,
      });
      this.emitUpdate();

      this.$store.dispatch('auditlog/write', {
        type: 'change_menu_product_photo',
        payload: {
          menu_id: this.menu._id,
          dish_name: this.dish.name,
          dish_id: this.dish._id,
          photo_url: null,
        },
      });
    },
    onStartImageUpload(upload_id) {
      this.$set(this.pending_uploads, upload_id, this.dish);
    },
    emitUpdate() {
      // this.$emit("update");
      this.$store.dispatch('menu/updateProduct');
    },
    onBaseWeightUpdated() {
      if (this.hasIikoIntegration && this.dish.weight_flag == 1) {
        let qr_value = this.dish.base_weight;
        let iiko_max_value = 1;

        if (this.dish.unit == 'g' || this.dish.unit == 'ml') {
          iiko_max_value = 1000;
        }

        const new_weighted_price =
          Math.round(
            (qr_value / iiko_max_value) *
              this.dish.external_weighted_price *
              100
          ) / 100;

        this.$set(this.dish, 'base_price', new_weighted_price);
      }

      this.emitUpdate();
    },
    onBasePriceUpdated() {
      this.emitUpdate();

      this.$store.dispatch('auditlog/write', {
        type: 'change_menu_product_price',
        payload: {
          menu_id: this.menu._id,
          dish_name: this.dish.name,
          dish_id: this.dish._id,
          price: this.dish.base_price,
        },
      });
    },
    onDishNameChanged() {
      this.emitUpdate();

      this.name_input_focused = false;

      this.$store.dispatch('auditlog/write', {
        type: 'change_menu_product_name',
        payload: {
          menu_id: this.menu._id,
          dish_name: this.dish.name,
          dish_id: this.dish._id,
        },
      });
    },
    duplicateDish() {
      this.$store.commit('menu/duplicateDish', {
        suffix: this.$t('menu_settings.duplicate_suffix'),
      });
      this.emitUpdate();
    },
    toggleExclusive() {
      this.$store.commit('menu/toggleExclusive', this.dish);
      this.emitUpdate();
    },
    toggleShowError() {
      this.$store.commit('menu/toggleHighlightErrors');
    },
    onPresetChanged(value) {
      const is_modificators_empty = !this.dish.price_groups.length;

      if (value == 'pizza' && is_modificators_empty) {
        this.$store.commit('menu/addPriceGroup', {
          name: 'Розмір',
          single_select: true,
          items: [
            { name: null, price: null, weight: null },
            { name: null, price: null, weight: null },
          ],
        });

        this.emitUpdate();
      }
    },
    onDishTransfered(to_another_menu) {
      this.chooseNearestProduct(this.category.name);
    },
    onPhotosEdited(urls) {
      this.$store.commit('menu/setProductData', {
        key: 'image',
        value: urls.widescreen_url,
      });
      this.$store.commit('menu/setProductData', {
        key: 'image_thumbnail',
        value: urls.identity_url,
      });
      this.$store.commit('menu/setProductData', {
        key: 'image_original',
        value: urls.original_url,
      });
      this.emitUpdate();
      this.dishEditorModal = false;
      this.$emit('imagesUpdated');
    },
  },
  computed: {
    menu() {
      return this.$store.state.menu.current;
    },
    dish() {
      return this.$store.state.menu.product;
    },
    isLocalizing() {
      return this.$store.getters['menu/isLocalizing'];
    },
    currentProductLocaleIndex() {
      return this.$store.getters['menu/currentProductLocaleIndex'];
    },
    currentProductLocale() {
      return this.$store.getters['menu/currentProductLocale'];
    },
    dishName: {
      get() {
        if (this.isLocalizing) {
          return this.currentProductLocale.name;
        }

        return this.dish.name;
      },
      set(value) {
        if (this.isLocalizing) {
          this.$store.commit('menu/setLocaleProductName', {
            locale_index: this.currentProductLocaleIndex,
            value,
          });
          return;
        }

        this.$store.commit('menu/setProductData', {
          key: 'name',
          value,
        });
      },
    },
    dishDescription: {
      get() {
        if (this.isLocalizing) {
          return this.currentProductLocale.description;
        }

        return this.dish.description;
      },
      set(value) {
        if (this.isLocalizing) {
          this.$store.commit('menu/setLocaleProductDescription', {
            locale_index: this.currentProductLocaleIndex,
            value,
          });
          return;
        }

        this.$store.commit('menu/setProductData', {
          key: 'description',
          value,
        });
      },
    },
    dishStock: {
      get() {
        return (
          !this.$store.getters['stoplist/isProductStoplisted'](
            this.menu._id,
            this.dish._id
          ) && this.dish.stock
        );
      },
      set(value) {
        if (value) {
          if (!this.dish.stock) {
            this.$store.commit('menu/setProductData', {
              key: 'stock',
              value,
            });

            this.emitUpdate();
          }

          this.$store.commit('menu/setProductData', {
            key: 'stock_count',
            value: -1,
          });

          this.$store.dispatch('stoplist/removeFromStoplist');
        } else {
          this.$store.dispatch('stoplist/addToStoplist');
        }
      },
    },
    dishExciseTag: {
      get() {
        return this.dish.is_excise;
      },
      set(value) {
        this.$store.commit('menu/setProductData', {
          key: 'is_excise',
          value,
        });

        this.emitUpdate();
      },
    },
    dishUnit: {
      get() {
        return this.dish.unit;
      },
      set(value) {
        this.$store.commit('menu/setProductData', {
          key: 'unit',
          value,
        });

        this.$store.commit('menu/setProductData', {
          key: 'unit_changed_at',
          value: new Date(),
        });

        this.emitUpdate();
      },
    },
    dishIsWeighted: {
      get() {
        return this.dish.weighted;
      },
      set(value) {
        this.$store.commit('menu/setProductData', {
          key: 'weighted',
          value,
        });
      },
    },
    dishWeightFlag: {
      get() {
        return this.dish.weight_flag;
      },
      set(value) {
        this.$store.commit('menu/setProductData', {
          key: 'weight_flag',
          value,
        });

        this.emitUpdate();
      },
    },
    dishTaxId: {
      get() {
        return this.dish.tax_id;
      },
      set(value) {
        this.$store.commit('menu/setProductData', {
          key: 'tax_id',
          value,
        });
        this.emitUpdate();
      },
    },
    dishPreset: {
      get() {
        return this.dish.preset;
      },
      set(value) {
        this.$store.commit('menu/setProductData', {
          key: 'preset',
          value,
        });
        this.onPresetChanged(value);
      },
    },
    dishWaitingTime: {
      get() {
        return this.dish.waiting_time || null;
      },
      set(value) {
        this.$store.commit('menu/setProductData', {
          key: 'waiting_time',
          value,
        });
      },
    },
    dishWaitingTimeHours: {
      get() {
        return Math.floor(this.dish.waiting_time / 60);
      },
      set(hrs) {
        const value = (this.dish.waiting_time % 60) + hrs * 60;

        this.$store.commit('menu/setProductData', {
          key: 'waiting_time',
          value,
        });
      },
    },
    dishWaitingTimeMinutes: {
      get() {
        return Math.floor(this.dish.waiting_time % 60);
      },
      set(mins) {
        const value = Math.floor(this.dish.waiting_time / 60) * 60 + mins;

        this.$store.commit('menu/setProductData', {
          key: 'waiting_time',
          value,
        });
      },
    },
    dishServiceDuration: {
      get() {
        return this.dish.service_duration;
      },
      set(value) {
        this.$store.commit('menu/setProductData', {
          key: 'service_duration',
          value,
        });
      },
    },
    dishServiceBreakTime: {
      get() {
        return this.dish.service_break_time;
      },
      set(value) {
        this.$store.commit('menu/setProductData', {
          key: 'service_break_time',
          value,
        });
      },
    },
    isSinglePriceOnly() {
      const variants_group = this.dish.price_groups.filter(
        (g) => g.single_select
      );
      return variants_group.length == 1 && variants_group[0].items.length == 1;
    },
    currency() {
      return this.$store.state.currency;
    },
    hasPosterIntegration() {
      return this.$store.getters.hasPosterIntegration;
    },
    hasIikoIntegration() {
      return this.$store.getters.hasIikoIntegration;
    },
    hasPosterPriceGroups() {
      return !!this.dish.price_groups.find((g) => {
        if (!g.single_select) return false;
        return !!g.items.find((i) => i.poster_product_id);
      });
    },
    blockPriceEdit() {
      if (this.hasPosterIntegration && this.dish.poster_product_id) {
        return true;
      }

      if (this.hasIikoIntegration && this.dish.iiko_product_id) {
        return true;
      }

      return false;
    },
    showBasePriceField() {
      if (this.blockPriceEdit) return true;

      const variants_group = this.dish.price_groups.find(
        (g) => g.single_select
      );

      if (this.dish.base_price > 0) return true;

      if (variants_group && variants_group.items.length) return false;

      return true;
    },
    isDishValid() {
      return this.$store.getters['menu/isDishValid'];
    },
    highlightErrors() {
      return this.$store.state.menu.highlight_errors;
    },
    showPackaging() {
      if (!this.isUsingRegularDishUi) return false;

      const cafe = this.$store.state.cafe;
      return (
        cafe.delivery.packaging_type == 'weighted' ||
        cafe.preorder.packaging_type == 'weighted'
      );
    },
    posterProductModifications() {
      if (!this.hasPosterIntegration) return [];
      if (!this.dish.poster_product_id) return [];

      const poster_product = this.$store.getters['poster/findPosterProduct'](
        this.dish.poster_product_id
      );

      if (!poster_product.price_groups.length) return [];

      const all_items = poster_product.price_groups.reduce(
        (acc, gr) => acc.concat(gr.items),
        []
      );

      return all_items;
    },
    canChangeStock() {
      if (!this.$store.state.cafe.poster) return true;
      return !this.$store.state.cafe.poster.sync_storage;
    },
    isProductSaving() {
      return this.$store.state.menu.is_product_updating;
    },
    showDishAR() {
      return this.$store.getters['integrations/isConnectedByTag']('aestar');
    },
    isUiMenuTranslationPreviewed() {
      return this.$store.state.menu.ui_translation_preview;
    },
    isUsingServiceReservations() {
      return this.$store.getters.isUsingServiceReservations;
    },
    isUsingRegularDishUi() {
      return !this.isUsingServiceReservations;
    },
  },
};
</script>

<style scoped>
.dish-img {
  width: 100%;
  object-fit: cover;
  height: 29vh;
  border-radius: 8px;
}

.waiting-time-flex {
  display: flex;
  width: 100%;
}

.header-settings {
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  align-self: center;
}

.dish-image-buttons {
  display: flex;
}

.desc-tooltip-translation {
  transform: translateY(-100%);
  top: 32px;
}
</style>
